import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useAutoDismissNotice } from '../../hooks/useAutoDismissNotice';
import {
  openRightDrawerTab,
  setZoomLevel,
  setSimulationPanelOpen,
  setWorkspaceView,
  toggleSimulationTrackNodes,
  updateGridSettings,
  toggleGrid,
} from '../../features/ui/uiSlice';
import { enterSimulationMode } from '../../features/ui/enterSimulationMode';
import {
  clearFloorPlan,
  setFloorPlan,
  setFloorPlanOpacity,
  toggleFloorPlanVisible,
} from '../../features/floorplan/floorplanSlice';
import { clearDxfImport } from '../../features/dxfImport/dxfImportSlice';
import { clearLayout, loadLayout, setIsDirty } from '../../features/layout/layoutSlice';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
// Temporarily disabled: popup was appearing even without real unsaved changes.
// import { UnsavedChangesModal } from './UnsavedChangesModal';
import { clearFacility } from '../../features/facility/facilitySlice';
import {
  clearEquipment,
  clearDxfEquipmentOverlays,
  loadEquipment,
  setEquipmentVisible,
  setLayoutSaveMarks,
  toggleShowPorts,
} from '../../features/equipment/equipmentSlice';
import {
  clearGeneratedDxfEquipmentOverlays,
  generateDxfEquipmentOverlays,
} from '../../features/equipment/generateDxfOverlaysThunk';
import { selectExportableEquipmentInstances } from '../../features/equipment/equipmentSelectors';
import { loadSimulationState, stopSimulation } from '../../features/simulation/simulationSlice';
import { loadLayersState } from '../../features/layers/layersSlice';
import { clearSelection } from '../../features/selection/selectionSlice';
import { clearHistory } from '../../features/history/historySlice';
import SessionsModal from '../auth/SessionsModal';
import { endAuthSession } from '../../utils/sessionLogout';
import { exportToJSON, importFromJSON } from '../../utils/storage';
import {
  buildLayoutSaveProject,
  createLayoutExportBase,
  equipmentLoadPayloadForLayout,
  extractEquipmentLayer,
  extractStockerMarkIds,
  fabExportFilename,
  mergeLiveDxfEquipmentIntoLayer,
  resolveEquipmentLayerForLoad,
  ensureEquipmentOwningLayersVisible,
  synchronizeLayoutEquipmentPorts,
} from '../../utils/projectExport';
import {
  computeEquipmentOverlayBounds,
  computeLayoutBoundsFromElements,
  normalizeImportedElements,
  orderFromProject,
} from '../../utils/layoutImport';
import { parseBackendDxfJson } from '../../utils/dxf';
import { setDxfEquipmentSource } from '../../utils/dxfEquipmentSource';
import { canvasChromeInsets, computeFitViewport } from '../../utils/flatPlanCamera';
import { viewportStore } from '../../store/viewportStore';
import { store } from '../../store';
import { computePolylineBBoxes, getRobustFitBounds } from '../../utils/floorplanHitTest';
import { clearFloor3dBatchCache, scheduleFloor3dBatchBuild } from '../../utils/floor3dBatchCache';
import type { DxfLayer, LayoutProject } from '../../types';
import { buildLayoutBom } from '../../utils/bom';
import { BomPreviewModal } from './BomPreviewModal';
import {
  fetchLayoutsForFloorPlan,
  fetchLayoutDetail,
  saveLayoutToBackend,
  LayoutSummary,
} from '../../utils/dxfApi';
import BriteFloLogo from '../icons/BriteFloLogo';
import { DEFAULT_MM_PER_CANVAS_UNIT } from '../../utils/layout3d';
import AmhsReportsModal from '../simulation/AmhsReportsModal';

type BackendDxfJsonDocument = Parameters<typeof parseBackendDxfJson>[0];

type MenuItem = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
};


function kickoffFloor3dBatches(payload: {
  polylines: number[][];
  polylineLayers: number[];
  polylineEntityColors: number[];
  layers: DxfLayer[];
}) {
  clearFloor3dBatchCache();
  void scheduleFloor3dBatchBuild({
    polylines: payload.polylines,
    polylineLayers: payload.polylineLayers,
    polylineEntityColors: payload.polylineEntityColors,
    layers: payload.layers,
    visible: true,
    darkMode: false,
  }).catch((err) => console.error('Prebuild 3D floor batches failed:', err));
}

const Toolbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceView = useAppSelector((state) => state.ui.workspaceView);
  const isMindmap = workspaceView === 'mindmap';

  const elements = useAppSelector((state) => state.layout.elements);
  const elementOrder = useAppSelector((state) => state.layout.elementOrder);
  const trackNodeAnnotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const projectMetadata = useAppSelector((state) => state.layout.projectMetadata);
  const viewport = useAppSelector((state) => ({ x: 0, y: 0, scale: state.ui.zoomLevel }));
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const showGrid = useAppSelector((state) => state.ui.showGrid);
  const floorPlanName = useAppSelector((state) => state.floorplan.name);
  const floorPlanId = useAppSelector((state) => state.floorplan.floorPlanId);
  const floorPlanVisible = useAppSelector((state) => state.floorplan.visible);
  const dxfImportActive = useAppSelector(
    (state) => state.dxfImport.active && state.dxfImport.phase !== 'error'
  );
  const layersState = useAppSelector((state) => state.layers);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const equipmentVisible = useAppSelector((state) => state.equipment.visible);
  const dxfOverlaysGenerated = useAppSelector((state) => state.equipment.dxfOverlaysGenerated);
  const exportableEquipmentInstances = useAppSelector(selectExportableEquipmentInstances);
  const showSimulationTrackNodes = useAppSelector((state) => state.ui.showSimulationTrackNodes);
  const equipmentShowLabels = useAppSelector((state) => state.equipment.showLabels);
  const equipmentShowPorts = useAppSelector((state) => state.equipment.showPorts);
  const layoutSaveMarkedIds = useAppSelector((state) => state.equipment.layoutSaveMarkedIds);
  // Select only the two fields used here. Subscribing to the whole simulation
  // slice re-rendered the toolbar on every live-stream batch and clock sync.
  const simulationSpeed = useAppSelector((state) => state.simulation.speed);
  const simulationVehicles = useAppSelector((state) => state.simulation.vehicles);
  const simulationPanelOpen = useAppSelector((state) => state.ui.simulationPanelOpen);
  const simulationRunning = useAppSelector((state) => state.simulation.running);
  /** A live stream is attached: the API owns the run, so leaving the tab must not stop it. */
  const liveStreamAttached = useAppSelector(
    (state) =>
      state.simulation.source === 'live' &&
      ['connecting', 'running', 'reconnecting', 'stopping'].includes(
        state.simulation.live.connectionStatus
      )
  );
  const leftSidebarOpen = useAppSelector((state) => state.ui.leftSidebarOpen);
  const rightSidebarOpen = useAppSelector((state) => state.ui.rightSidebarOpen);
  const mmPerCanvasUnit = useAppSelector(
    (state) => state.floorplan.millimetersPerCanvasUnit ?? DEFAULT_MM_PER_CANVAS_UNIT
  );
  const floorPlanScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const floorPlanDxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const floorPlanBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorPlanPolylines = useAppSelector((state) => state.floorplan.polylines);
  const floorPlanCanvasUnitsPerMeter = useAppSelector(
    (state) => state.floorplan.canvasUnitsPerMeter
  );
  const toolboxItems = useAppSelector((state) => state.toolbox.items);

  const backendJsonInputRef = useRef<HTMLInputElement>(null);
  const layoutJsonInputRef = useRef<HTMLInputElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [isImportingBackendJson, setIsImportingBackendJson] = useState(false);
  const [isImportingLayoutJson, setIsImportingLayoutJson] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
  const [toolbarNotice, setToolbarNotice] = useState<string | null>(null);
  const clearToolbarNotice = useCallback(() => setToolbarNotice(null), []);
  useAutoDismissNotice(toolbarNotice, clearToolbarNotice);
  const [bomModalOpen, setBomModalOpen] = useState(false);
  const [reportsModalOpen, setReportsModalOpen] = useState(false);
  const hasAmhsReports = useAppSelector(
    (state) =>
      state.simulation.amhsReports != null ||
      state.simulation.live.liveReport != null ||
      state.simulation.lastLiveReport != null
  );
  const [loadSavedLayoutOpen, setLoadSavedLayoutOpen] = useState(false);
  const [layouts, setLayouts] = useState<LayoutSummary[]>([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>('');
  const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [layoutFetchError, setLayoutFetchError] = useState<string | null>(null);
  const [saveLayoutOpen, setSaveLayoutOpen] = useState(false);
  const [saveLayoutName, setSaveLayoutName] = useState('');
  const [layoutSaveError, setLayoutSaveError] = useState<string | null>(null);
  const [currentLayoutId, setCurrentLayoutId] = useState<string | null>(null);
  const [currentLayoutName, setCurrentLayoutName] = useState<string | null>(null);
  const [saveSelectedLayoutId, setSaveSelectedLayoutId] = useState<string>('');
  const [isSavingNewLayout, setIsSavingNewLayout] = useState(false);
  const [isUpdatingLayout, setIsUpdatingLayout] = useState(false);

  const {
    // showModal: showUnsavedChangesModal,
    requestAction: requestGuardedAction,
    // handleStay: handleStayUnsavedChanges,
    // handleLeave: handleLeaveUnsavedChanges,
  } = useUnsavedChangesGuard();

  const layoutBom = useMemo(
    () =>
      buildLayoutBom({
        elements,
        toolboxItems,
        calibration: {
          floorplanBounds: Boolean(floorPlanBounds),
          floorplanPolylineCount: floorPlanPolylines.length,
          scaleFactor: floorPlanScaleFactor,
          dxfUnitsPerMeter: floorPlanDxfUnitsPerMeter,
          millimetersPerCanvasUnit: mmPerCanvasUnit,
          canvasUnitsPerMeter: floorPlanCanvasUnitsPerMeter,
        },
      }),
    [
      elements,
      toolboxItems,
      floorPlanBounds,
      floorPlanPolylines.length,
      floorPlanScaleFactor,
      floorPlanDxfUnitsPerMeter,
      mmPerCanvasUnit,
      floorPlanCanvasUnitsPerMeter,
    ]
  );

  const fitBoundsToCanvas = (bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  }) => {
    const vp = viewportStore.getState();
    const insets = canvasChromeInsets({ leftSidebarOpen, rightSidebarOpen });
    const fit = computeFitViewport(
      bounds,
      vp.canvasWidth || 1200,
      vp.canvasHeight || 800,
      0.9,
      insets
    );
    viewportStore.setState(fit);
    dispatch(setZoomLevel(fit.zoom));
  };

  /**
   * View → Generate/Clear Overlays. Generation reads the imported DXF and keeps
   * only overlays on visible layers; clearing destroys them so they cannot be
   * saved or simulated.
   */
  const handleToggleGeneratedOverlays = () => {
    if (dxfOverlaysGenerated) {
      dispatch(clearGeneratedDxfEquipmentOverlays());
      setToolbarNotice('Cleared generated equipment overlays.');
      return;
    }
    const result = dispatch(generateDxfEquipmentOverlays());
    setToolbarNotice(result.message);
    if (result.ok && result.count > 0) {
      dispatch(openRightDrawerTab('equipment'));
    }
  };

  const buildSavedLayoutProject = (layoutBase: LayoutProject) =>
    buildLayoutSaveProject({
      layoutBase,
      templates: Object.values(equipmentTemplates),
      instances: exportableEquipmentInstances,
      markedIds: layoutSaveMarkedIds,
      floorPlanId,
      floorPlanName,
      equipmentUi: {
        visible: equipmentVisible,
        showLabels: equipmentShowLabels,
        showPorts: equipmentShowPorts,
      },
    });

  const handleExport = () => {
    const exportableSimulation =
      Object.keys(simulationVehicles).length > 0
        ? {
          speed: simulationSpeed,
          vehicles: Object.fromEntries(
            Object.entries(simulationVehicles).map(([id, v]) => [
              id,
              {
                reversed: v.reversed,
                stopNodeId: v.stopNodeId,
                speedFactor: v.speedFactor,
                stopDurationS: v.stopDurationS,
                stops: v.stops.map((s) => ({
                  id: s.id,
                  railId: s.railId,
                  distance: s.distance,
                  durationS: s.durationS,
                  loaded: s.loaded,
                })),
              },
            ])
          ),
        }
        : undefined;

    const layoutBase = createLayoutExportBase({
      elements,
      elementOrder,
      layers: layersState.layers,
      layerOrder: layersState.layerOrder,
      groups: layersState.groups,
      viewport,
      gridSettings,
      simulation: exportableSimulation,
      trackNodeAnnotations,
    });

    const project = buildSavedLayoutProject(layoutBase);
    const projectWithMeta: LayoutProject = {
      ...project,
      metadata: {
        ...(projectMetadata || {}),
        ...project.metadata,
      },
    };

    exportToJSON(projectWithMeta, fabExportFilename(floorPlanName));
    dispatch(setIsDirty(false));
    const equipmentSavedCount = projectWithMeta.equipment?.instances.length ?? 0;
    setToolbarNotice(
      equipmentSavedCount > 0
        ? `Saved layout with ${equipmentSavedCount} equipment overlay(s).`
        : 'Saved layout.'
    );
  };

  const applyLayoutProject = (project: LayoutProject) => {
    dispatch(setIsDirty(false));
    if (simulationRunning) {
      dispatch(stopSimulation());
    }

    const order = orderFromProject(project);
    const { elements: normalizedElements, order: normalizedOrder } = normalizeImportedElements(
      project.elements,
      order,
      mmPerCanvasUnit
    );

    // Saved equipment overlays load into the store. Layout-saved footprints paint
    // immediately; DXF overlays stay hidden until Identify Equipment / Overlay.
    const fromProject = resolveEquipmentLayerForLoad(project, normalizedElements);
    const equipmentLayer = mergeLiveDxfEquipmentIntoLayer(
      fromProject,
      Object.values(equipmentInstances),
      Object.values(equipmentTemplates)
    );
    const synchronizedElements = synchronizeLayoutEquipmentPorts(
      normalizedElements,
      equipmentLayer?.instances
    );
    const { elements: layoutElements, layers: visibleLayers } = ensureEquipmentOwningLayersVisible(
      project.layers as Record<string, { visible?: boolean }> | undefined,
      synchronizedElements,
      normalizedOrder
    );
    const layoutOrder = normalizedOrder;

    dispatch(
      loadLayout({
        elements: layoutElements,
        order: layoutOrder,
        layers: (visibleLayers as typeof project.layers) ?? project.layers,
        layerOrder: project.layerOrder,
        trackNodeAnnotations: project.trackNodeAnnotations,
        projectMetadata: (project.metadata as Record<string, unknown>) || null,
      })
    );

    const layersWithElements = Object.fromEntries(
      Object.entries((visibleLayers as typeof project.layers) || project.layers || {}).map(
        ([id, layer]) => [id, { ...layer, id, elementIds: [] as string[] }]
      )
    );
    layoutOrder.forEach((elementId) => {
      const element = layoutElements[elementId];
      if (!element) return;
      const layerId = layersWithElements[element.layerId]
        ? element.layerId
        : Object.keys(layersWithElements)[0];
      if (!layerId || !layersWithElements[layerId]) return;
      layersWithElements[layerId].elementIds.push(elementId);
    });

    dispatch(
      loadLayersState({
        layers: Object.keys(layersWithElements).length > 0 ? layersWithElements : undefined,
        layerOrder: project.layerOrder,
        groups: project.groups ?? {},
      })
    );

    // Overlay catalog in store: layout-saved paint now; DXF waits for Identify.
    if (equipmentLayer) {
      dispatch(loadEquipment(equipmentLoadPayloadForLayout(equipmentLayer)));
    } else {
      dispatch(clearEquipment());
      dispatch(setEquipmentVisible(false));
    }

    const layoutSaveMarkIds = extractStockerMarkIds(project);
    dispatch(setLayoutSaveMarks(layoutSaveMarkIds));

    if (project.simulation) {
      dispatch(
        loadSimulationState({
          speed: project.simulation.speed,
          vehicles: Object.fromEntries(
            Object.entries(project.simulation.vehicles || {}).map(([id, v]) => [
              id,
              {
                reversed: v.reversed,
                stopNodeId: v.stopNodeId,
                speedFactor: v.speedFactor,
                stopDurationS: v.stopDurationS,
                stops: (v.stops || []).map((s) => ({
                  id: s.id,
                  railId: s.railId,
                  distance: s.distance,
                  durationS: s.durationS,
                  loaded:
                    (s as { loaded?: boolean }).loaded ?? (s as { setloaded?: boolean }).setloaded,
                })),
              },
            ])
          ),
        })
      );
    }

    if (project.gridSettings) {
      dispatch(updateGridSettings(project.gridSettings));
    }

    dispatch(clearSelection());
    dispatch(clearHistory());

    // Prefer robust DXF framing (ignores distant outliers). Fall back to layout
    // elements, then equipment-only overlays so loads still frame content.
    const elementBounds = computeLayoutBoundsFromElements(layoutElements, layoutOrder);
    const layoutBounds =
      elementBounds ?? computeEquipmentOverlayBounds(equipmentLayer?.instances ?? []);
    const fpState = store.getState().floorplan;
    const hasDxfGeometry = fpState.polylines.length > 0 && !!fpState.bounds;
    const robustDxfBounds = hasDxfGeometry
      ? getRobustFitBounds(fpState.bounds, computePolylineBBoxes(fpState.polylines))
      : null;
    const fitBounds = robustDxfBounds ?? (hasDxfGeometry ? fpState.bounds : null) ?? layoutBounds;

    if (fitBounds) {
      const applyFit = () => {
        const vp = viewportStore.getState();
        const insets = canvasChromeInsets({
          leftSidebarOpen: store.getState().ui.leftSidebarOpen,
          rightSidebarOpen: store.getState().ui.rightSidebarOpen,
        });
        const fit = computeFitViewport(
          fitBounds,
          vp.canvasWidth || 1200,
          vp.canvasHeight || 800,
          0.9,
          insets
        );
        viewportStore.setState(fit);
        dispatch(setZoomLevel(fit.zoom));
      };
      applyFit();
      // Heavy loadLayout/equipment reconcile can overwrite framing — refit after paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(applyFit);
      });
      window.setTimeout(applyFit, 0);
      window.setTimeout(applyFit, 80);
      window.setTimeout(applyFit, 250);
    }

    const equipmentCount = equipmentLayer?.instances.length ?? 0;
    setToolbarNotice(
      equipmentCount > 0
        ? `Loaded layout “${project.metadata?.name || 'Untitled'}” (${normalizedOrder.length} elements, ${equipmentCount} equipment overlay(s)).`
        : layoutSaveMarkIds.length > 0
          ? `Loaded layout “${project.metadata?.name || 'Untitled'}” (${normalizedOrder.length} elements, ${layoutSaveMarkIds.length} overlay item(s) in save list).`
          : `Loaded layout “${project.metadata?.name || 'Untitled'}” (${normalizedOrder.length} elements).`
    );
  };

  useEffect(() => {
    if ((!loadSavedLayoutOpen && !saveLayoutOpen) || !floorPlanId) return;
    const loadLayouts = async () => {
      setIsLoadingLayouts(true);
      setLayoutFetchError(null);
      try {
        const response = await fetchLayoutsForFloorPlan({ floorPlanId });
        setLayouts(response.items || []);

        if (response.items?.length > 0) {
          setSelectedLayoutId(response.items[0].id);
          setSaveSelectedLayoutId(response.items[0].id);
          setSaveLayoutName(response.items[0].name);
        } else {
          setSelectedLayoutId('');
          setSaveSelectedLayoutId('');
          setSaveLayoutName(floorPlanName ? `Layout for ${floorPlanName}` : 'Untitled Layout');
        }
      } catch (err) {
        console.error('Failed to load layouts:', err);
        setLayoutFetchError('Failed to fetch layouts for this floor plan.');
      } finally {
        setIsLoadingLayouts(false);
      }
    };
    void loadLayouts();
  }, [loadSavedLayoutOpen, saveLayoutOpen, floorPlanId]);

  const handleLoadSavedLayout = async () => {
    if (!selectedLayoutId) return;
    setIsLoadingDetail(true);
    setLayoutFetchError(null);
    try {
      const project = await fetchLayoutDetail({ layoutId: selectedLayoutId });
      const savedOverlayCount = extractEquipmentLayer(project)?.instances?.length ?? 0;
      const savedElementCount = Object.keys(project.elements ?? {}).length;
      // An equipment-only layout is still a layout — it just has no elements.
      if (savedElementCount === 0 && savedOverlayCount === 0) {
        setLayoutFetchError('No layout elements or equipment overlays found in this saved layout.');
        return;
      }
      applyLayoutProject(project);
      setCurrentLayoutId(selectedLayoutId);
      setCurrentLayoutName(project.metadata?.name || 'Untitled Layout');
      setLoadSavedLayoutOpen(false);
      setToolbarNotice(
        `Loaded saved layout “${project.metadata?.name || 'Untitled'}” (${savedElementCount} elements, ${savedOverlayCount} equipment overlay(s)).`
      );
    } catch (err) {
      console.error('Failed to load saved layout details:', err);
      setLayoutFetchError('Failed to load layout details.');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleOpenSaveLayoutModal = () => {
    if (currentLayoutId) {
      setSaveSelectedLayoutId(currentLayoutId);
      setSaveLayoutName(currentLayoutName || 'Untitled Layout');
    } else if (layouts.length > 0) {
      setSaveSelectedLayoutId(layouts[0].id);
      setSaveLayoutName(layouts[0].name);
    } else {
      setSaveSelectedLayoutId('');
      setSaveLayoutName(floorPlanName ? `Layout for ${floorPlanName}` : 'Untitled Layout');
    }
    setLayoutSaveError(null);
    setSaveLayoutOpen(true);
  };

  const buildLayoutPayload = (name: string, overrideId?: string | null) => {
    const exportableSimulation =
      Object.keys(simulationVehicles).length > 0
        ? {
          speed: simulationSpeed,
          vehicles: Object.fromEntries(
            Object.entries(simulationVehicles).map(([id, v]) => [
              id,
              {
                reversed: v.reversed,
                stopNodeId: v.stopNodeId,
                speedFactor: v.speedFactor,
                stopDurationS: v.stopDurationS,
                stops: v.stops.map((s) => ({
                  id: s.id,
                  railId: s.railId,
                  distance: s.distance,
                  durationS: s.durationS,
                  loaded: s.loaded,
                })),
              },
            ])
          ),
        }
        : undefined;

    const layoutBase = createLayoutExportBase({
      elements,
      elementOrder,
      layers: layersState.layers,
      layerOrder: layersState.layerOrder,
      groups: layersState.groups,
      viewport,
      gridSettings,
      simulation: exportableSimulation,
      trackNodeAnnotations,
    });

    const project = buildSavedLayoutProject(layoutBase);

    return {
      ...project,
      floorPlanId,
      ...(overrideId ? { id: overrideId } : {}),
      metadata: {
        ...(projectMetadata || {}),
        ...project.metadata,
        name: name.trim(),
      },
    };
  };

  const handleSaveNew = async () => {
    if (!floorPlanId) {
      setLayoutSaveError('No floor plan loaded. Cannot save layout.');
      return;
    }
    if (!saveLayoutName.trim()) {
      setLayoutSaveError('Please enter a layout name.');
      return;
    }

    setIsSavingNewLayout(true);
    setLayoutSaveError(null);

    try {
      const layoutPayload = buildLayoutPayload(saveLayoutName.trim());
      const res = await saveLayoutToBackend({
        layoutPayload,
        layoutId: null,
      });

      if (res && res.id) {
        setCurrentLayoutId(res.id);
        setCurrentLayoutName(res.metadata?.name || saveLayoutName.trim());
      }

      dispatch(setIsDirty(false));
      setSaveLayoutOpen(false);
      setToolbarNotice(`Layout “${saveLayoutName.trim()}” saved successfully as new.`);
    } catch (err) {
      console.error('Failed to save new layout:', err);
      setLayoutSaveError('Failed to save new layout to backend.');
    } finally {
      setIsSavingNewLayout(false);
    }
  };

  const handleUpdateExisting = async () => {
    if (!floorPlanId) {
      setLayoutSaveError('No floor plan loaded. Cannot save layout.');
      return;
    }
    if (!saveSelectedLayoutId) {
      setLayoutSaveError('Please select a layout to update.');
      return;
    }
    if (!saveLayoutName.trim()) {
      setLayoutSaveError('Please enter a layout name.');
      return;
    }

    setIsUpdatingLayout(true);
    setLayoutSaveError(null);

    try {
      const layoutPayload = buildLayoutPayload(saveLayoutName.trim(), saveSelectedLayoutId);
      const res = await saveLayoutToBackend({
        layoutPayload,
        layoutId: saveSelectedLayoutId,
      });

      if (res && res.id) {
        setCurrentLayoutId(res.id);
        setCurrentLayoutName(res.metadata?.name || saveLayoutName.trim());
      }

      dispatch(setIsDirty(false));
      setSaveLayoutOpen(false);
      setToolbarNotice(`Layout “${saveLayoutName.trim()}” updated successfully.`);
    } catch (err) {
      console.error('Failed to update layout:', err);
      setLayoutSaveError('Failed to update layout in backend.');
    } finally {
      setIsUpdatingLayout(false);
    }
  };

  const handleImportLayoutJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImportingLayoutJson(true);
    try {
      const parsed = await importFromJSON(file);
      if (parsed.type === 'equipment') {
        setToolbarNotice(
          'This file is equipment-only. Use Load Layout with a layout export (elements + layers).'
        );
        return;
      }

      const project = parsed.project;
      const elementCount =
        'elements' in project && project.elements ? Object.keys(project.elements).length : 0;
      // An equipment-only layout export has no elements but is still loadable.
      if (elementCount === 0 && (extractEquipmentLayer(project)?.instances?.length ?? 0) === 0) {
        setToolbarNotice('No layout elements or equipment overlays found in this JSON file.');
        return;
      }

      applyLayoutProject({
        version:
          'version' in project && typeof project.version === 'string' ? project.version : '1.0.0',
        metadata:
          'metadata' in project && project.metadata
            ? project.metadata
            : {
              name: file.name.replace(/\.json$/i, ''),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
        elements: project.elements,
        elementOrder: 'elementOrder' in project ? project.elementOrder : undefined,
        layers: 'layers' in project && project.layers ? project.layers : {},
        layerOrder: 'layerOrder' in project ? project.layerOrder : undefined,
        groups: 'groups' in project ? project.groups : undefined,
        viewport:
          'viewport' in project && project.viewport ? project.viewport : { x: 0, y: 0, scale: 1 },
        gridSettings:
          'gridSettings' in project && project.gridSettings ? project.gridSettings : gridSettings,
        trackNodeAnnotations:
          'trackNodeAnnotations' in project ? project.trackNodeAnnotations : undefined,
        stockers: 'stockers' in project ? project.stockers : undefined,
        simulation: 'simulation' in project ? project.simulation : undefined,
        equipment: 'equipment' in project ? project.equipment : undefined,
      });
      setCurrentLayoutId(null);
      setCurrentLayoutName(null);
    } catch (err) {
      console.error('Failed to import layout JSON:', err);
      setToolbarNotice('Could not parse this layout JSON. Expected a fab-planner layout export.');
    } finally {
      setIsImportingLayoutJson(false);
    }
  };

  const handleImportBackendJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImportingBackendJson(true);
    try {
      const text = await file.text();
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const doc = JSON.parse(text) as BackendDxfJsonDocument;

      const floorPlan = parseBackendDxfJson(doc);
      const {
        polylines,
        bounds,
        scaleFactor,
        dxfUnit,
        dxfUnitsPerMeter,
        calibration,
        millimetersPerCanvasUnit,
        canvasUnitsPerMeter,
        layers,
        polylineLayers,
        polylineMeta,
        labels,
        transform,
      } = floorPlan;

      if (polylines.length === 0) {
        setToolbarNotice('No drawable geometry found in this backend JSON file.');
        return;
      }

      dispatch(setFloorPlanOpacity(1));
      dispatch(
        setFloorPlan({
          name: file.name,
          polylines,
          bounds,
          scaleFactor,
          dxfUnit,
          dxfUnitsPerMeter,
          calibration,
          millimetersPerCanvasUnit,
          canvasUnitsPerMeter,
          layers,
          polylineLayers,
          polylineMeta,
          labels,
          transform,
        })
      );

      // Overlays are generated on demand from this document (View → Generate
      // Overlays), never automatically on import.
      setDxfEquipmentSource({ document: doc, transform });
      dispatch(clearDxfEquipmentOverlays({ clearReviewDecisions: true }));

      kickoffFloor3dBatches({
        polylines,
        polylineLayers,
        polylineEntityColors: polylineMeta.map((m) => m.color),
        layers,
      });
      fitBoundsToCanvas(bounds);
      dispatch(openRightDrawerTab('dxf-layers'));
    } catch (err) {
      console.error('Failed to import backend JSON:', err);
      setToolbarNotice(
        'Could not parse this backend JSON file. Please ensure it is valid parser output.'
      );
    } finally {
      setIsImportingBackendJson(false);
    }
  };

  const handleLogoutActual = async () => {
    clearFloor3dBatchCache();
    dispatch(stopSimulation());
    dispatch(clearFacility());
    dispatch(clearEquipment());
    dispatch(clearDxfImport());
    dispatch(clearFloorPlan());
    dispatch(clearLayout());
    try {
      localStorage.removeItem('layout_editor_equipment');
    } catch (e) {
      console.error('Failed to clear equipment local storage:', e);
    }
    setCurrentLayoutId(null);
    setCurrentLayoutName(null);
    // Revoke refresh cookie + erase access token; App then shows sign-in.
    await endAuthSession();
    navigate('/');
  };

  const handleLogout = () => {
    requestGuardedAction(handleLogoutActual);
  };

  const handleReturnToLandingActual = () => {
    clearFloor3dBatchCache();
    dispatch(stopSimulation());
    dispatch(clearFacility());
    dispatch(clearLayout());
    dispatch(clearEquipment());
    dispatch(clearDxfImport());
    dispatch(clearFloorPlan());
    try {
      localStorage.removeItem('layout_editor_equipment');
    } catch (e) {
      console.error('Failed to clear equipment local storage:', e);
    }
    setCurrentLayoutId(null);
    setCurrentLayoutName(null);
    navigate('/');
  };

  const handleReturnToLanding = () => {
    requestGuardedAction(handleReturnToLandingActual);
  };

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
        setLogoutConfirmOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [profileMenuOpen]);

  const fileMenu: MenuItem[] = [
    { label: 'Save', onClick: handleExport, icon: <SaveIcon /> },
    {
      label: 'Save Layout',
      onClick: handleOpenSaveLayoutModal,
      disabled: !floorPlanId,
      icon: <SaveIcon />,
    },
    {
      label: isImportingBackendJson ? 'Loading…' : 'Load DXF JSON',
      onClick: () => requestGuardedAction(() => backendJsonInputRef.current?.click()),
      disabled: isImportingBackendJson || dxfImportActive,
      icon: <LoadIcon />,
    },
    {
      label: isImportingLayoutJson ? 'Loading layout…' : 'Load Layout',
      onClick: () => requestGuardedAction(() => layoutJsonInputRef.current?.click()),
      disabled: isImportingLayoutJson || dxfImportActive,
      icon: <LoadLayoutIcon />,
    },
    {
      label: 'Load Saved Layout',
      onClick: () => requestGuardedAction(() => setLoadSavedLayoutOpen(true)),
      disabled: !floorPlanId || dxfImportActive,
      icon: <LoadLayoutIcon />,
    },
  ];

  const viewMenu: MenuItem[] = [
    {
      label: showGrid ? 'Hide Grid' : 'Show Grid',
      onClick: () => dispatch(toggleGrid()),
      icon: <GridIcon />,
    },
    {
      label: dxfOverlaysGenerated ? 'Clear Overlays' : 'Generate Overlays',
      onClick: handleToggleGeneratedOverlays,
      disabled: !dxfOverlaysGenerated && !floorPlanName,
    },
    {
      label: showSimulationTrackNodes ? 'Hide Nodes' : 'Show Nodes',
      onClick: () => dispatch(toggleSimulationTrackNodes()),
    },
    {
      // Same flag the Labels popup and Equipment library use, so the three stay in step.
      // Drives the load-port markers on stockers, tools and equipment overlays.
      label: equipmentShowPorts ? 'Hide Load Ports' : 'Show Load Ports',
      onClick: () => dispatch(toggleShowPorts()),
    },
    {
      label: floorPlanVisible ? 'Hide Floor Plan' : 'Floor Plan',
      onClick: () => dispatch(toggleFloorPlanVisible()),
      disabled: !floorPlanName,
    },
    {
      label: 'Generate BOM',
      onClick: () => setBomModalOpen(true),
    },
  ];

  const menus: Array<{ id: string; label: string; items: MenuItem[] }> = [
    { id: 'file', label: 'File', items: fileMenu },
    { id: 'view', label: 'View', items: viewMenu },
  ];

  return (
    <>
      <div className="relative h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center px-5">
        {/* Left: brand + menus */}
        <div className="flex items-center gap-6 shrink-0">
          <button
            type="button"
            onClick={handleReturnToLanding}
            title="Return to projects"
            aria-label="Peakaso — return to projects"
            className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-800"
          >
            <BriteFloLogo size={21} />
            <span className="text-base font-semibold text-slate-900 dark:text-gray-100 tracking-tight">
              Peakaso
            </span>
          </button>

          <nav className="flex items-center gap-1">
            {menus.map((menu) => (
              <TopMenu
                key={menu.id}
                id={menu.id}
                label={menu.label}
                items={menu.items}
                open={openMenu === menu.id}
                onToggle={() => setOpenMenu((prev) => (prev === menu.id ? null : menu.id))}
                onClose={() => setOpenMenu(null)}
              />
            ))}
          </nav>
        </div>

        {/* Spacer pushes toggle + utilities to the right */}
        <div className="flex-1" />

        {/* Right group: Editor/Simulator switch, Map/Mind Map, theme + user */}
        <div className="flex items-center gap-6 shrink-0">
          <div className="flex items-center rounded-full bg-slate-100 dark:bg-gray-800 p-0.5">
            <SegmentButton
              active={!simulationPanelOpen}
              onClick={() => {
                // Local playback stops with the tab. A live stream keeps
                // running on the API and stays attached; use Stop live to end it.
                if (simulationRunning && !liveStreamAttached) {
                  dispatch(stopSimulation());
                }
                dispatch(setSimulationPanelOpen(false));
              }}
            >
              Editor
            </SegmentButton>
            <SegmentButton
              active={simulationPanelOpen}
              onClick={() => {
                // Leave any active editor tool / resize chrome before Simulator.
                dispatch(enterSimulationMode());
              }}
            >
              <SimulateIcon />
              Simulator
            </SegmentButton>
          </div>

          <div className="flex items-center rounded-full bg-slate-100 dark:bg-gray-800 p-0.5">
            <SegmentButton active={!isMindmap} onClick={() => dispatch(setWorkspaceView('map'))}>
              Map
            </SegmentButton>
            <SegmentButton active={isMindmap} onClick={() => dispatch(setWorkspaceView('mindmap'))}>
              <MindMapIcon />
              Mind Map
            </SegmentButton>
          </div>

          <button
            type="button"
            title={
              hasAmhsReports ? 'Open AMHS simulation reports' : 'Run a simulation to load reports'
            }
            onClick={() => setReportsModalOpen(true)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${hasAmhsReports
                ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200'
                : 'border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-500'
              }`}
          >
            <ReportsIcon />
            Reports
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              title="Toggle dark mode"
              aria-label="Toggle dark mode"
              className="flex h-8 w-8 items-center justify-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            >
              <MoonIcon />
            </button>
            <div ref={profileMenuRef} className="relative">
              <button
                type="button"
                title="Account"
                aria-label="Account"
                onClick={() => {
                  setOpenMenu(null);
                  setProfileMenuOpen((open) => !open);
                }}
                className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${profileMenuOpen
                    ? 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-slate-200'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
              >
                <UserIcon />
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 min-w-[150px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={() => {
                      setLogoutConfirmOpen(false);
                      setProfileMenuOpen(false);
                      navigate('/profile');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    <UserIcon />
                    Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setSessionsModalOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    <UserIcon />
                    Devices
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setLogoutConfirmOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    <LogoutIcon />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {toolbarNotice && (
          <div className="absolute right-5 top-full z-50 mt-2 max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 shadow-lg dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-100">
            <div className="flex items-start gap-3">
              <span className="flex-1">{toolbarNotice}</span>
              <button
                type="button"
                aria-label="Dismiss message"
                onClick={() => setToolbarNotice(null)}
                className="text-amber-700 hover:text-amber-900 dark:text-amber-200 dark:hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <input
          ref={backendJsonInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleImportBackendJson}
          className="hidden"
        />
        <input
          ref={layoutJsonInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleImportLayoutJson}
          className="hidden"
        />
      </div>

      {logoutConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-900"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200">
                <LogoutIcon />
              </div>
              <div>
                <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Log out?
                </div>
                <div className="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-400">
                  This will close the current workspace and return you to the login screen.
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  handleLogout();
                }}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {sessionsModalOpen && <SessionsModal onClose={() => setSessionsModalOpen(false)} />}

      <AmhsReportsModal open={reportsModalOpen} onClose={() => setReportsModalOpen(false)} />

      {bomModalOpen && <BomPreviewModal bom={layoutBom} onClose={() => setBomModalOpen(false)} />}

      {loadSavedLayoutOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 backdrop-blur-sm px-4"
          onClick={() => setLoadSavedLayoutOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Load Saved Layout
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Select a saved layout from the database to load.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setLoadSavedLayoutOpen(false)}
                className="toolbar-button text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl font-bold"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="py-6 flex flex-col gap-4">
              {isLoadingLayouts ? (
                <div className="flex flex-col items-center justify-center py-6 gap-2">
                  <svg
                    className="animate-spin h-8 w-8 text-sky-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Fetching saved layouts...
                  </span>
                </div>
              ) : layoutFetchError ? (
                <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900">
                  {layoutFetchError}
                </div>
              ) : layouts.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                  No saved layouts found for this floor plan.
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="saved-layout-select"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Select Layout
                  </label>
                  <select
                    id="saved-layout-select"
                    value={selectedLayoutId}
                    onChange={(e) => setSelectedLayoutId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    {layouts.map((layout) => (
                      <option key={layout.id} value={layout.id}>
                        {layout.name} ({new Date(layout.updatedAt).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setLoadSavedLayoutOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                disabled={isLoadingDetail}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLoadSavedLayout}
                disabled={!selectedLayoutId || isLoadingLayouts || isLoadingDetail}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 ${!selectedLayoutId || isLoadingLayouts || isLoadingDetail
                    ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed text-gray-500 dark:text-gray-400'
                    : 'bg-sky-600 hover:bg-sky-700'
                  }`}
              >
                {isLoadingDetail ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  'Load'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {saveLayoutOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 backdrop-blur-sm px-4"
          onClick={() => setSaveLayoutOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Save Layout
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter a name to save this layout to the database.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSaveLayoutOpen(false)}
                className="toolbar-button text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl font-bold"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="py-6 flex flex-col gap-4">
              {layoutSaveError && (
                <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900">
                  {layoutSaveError}
                </div>
              )}
              {isLoadingLayouts ? (
                <div className="flex flex-col items-center justify-center py-6 gap-2">
                  <svg
                    className="animate-spin h-8 w-8 text-sky-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Fetching existing layouts...
                  </span>
                </div>
              ) : (
                <>
                  {layouts.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="save-layout-select"
                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Select Existing Layout (to Update)
                      </label>
                      <select
                        id="save-layout-select"
                        value={saveSelectedLayoutId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSaveSelectedLayoutId(id);
                          const selected = layouts.find((l) => l.id === id);
                          if (selected) {
                            setSaveLayoutName(selected.name);
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        disabled={isSavingNewLayout || isUpdatingLayout}
                      >
                        {layouts.map((layout) => (
                          <option key={layout.id} value={layout.id}>
                            {layout.name} ({new Date(layout.updatedAt).toLocaleDateString()})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 p-2.5 rounded-lg border border-amber-200 dark:border-amber-900">
                      No existing layouts found for this floor plan. You can only save as a new
                      layout.
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="save-layout-name-input"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Layout Name
                    </label>
                    <input
                      id="save-layout-name-input"
                      type="text"
                      value={saveLayoutName}
                      onChange={(e) => setSaveLayoutName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="Enter layout name"
                      disabled={isSavingNewLayout || isUpdatingLayout}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setSaveLayoutOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                disabled={isSavingNewLayout || isUpdatingLayout}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNew}
                disabled={!saveLayoutName.trim() || isSavingNewLayout || isUpdatingLayout}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 ${!saveLayoutName.trim() || isSavingNewLayout || isUpdatingLayout
                    ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed text-gray-500 dark:text-gray-400'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
              >
                {isSavingNewLayout ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                type="button"
                onClick={handleUpdateExisting}
                disabled={
                  !saveSelectedLayoutId ||
                  !saveLayoutName.trim() ||
                  isSavingNewLayout ||
                  isUpdatingLayout
                }
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 ${!saveSelectedLayoutId ||
                    !saveLayoutName.trim() ||
                    isSavingNewLayout ||
                    isUpdatingLayout
                    ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed text-gray-500 dark:text-gray-400'
                    : 'bg-sky-600 hover:bg-sky-700'
                  }`}
              >
                {isUpdatingLayout ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temporarily disabled: popup was appearing even without real unsaved changes.
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onStay={handleStayUnsavedChanges}
        onLeave={handleLeaveUnsavedChanges}
      />
      */}
    </>
  );
};

interface TopMenuProps {
  id: string;
  label: string;
  items: MenuItem[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const TopMenu: React.FC<TopMenuProps> = ({ label, items, open, onToggle, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeItems = useMemo(
    () => items.filter((item) => item.label.toLowerCase() !== 'coming soon'),
    [items]
  );
  const hasItems = activeItems.length > 0;

  useEffect(() => {
    if (!open || !hasItems) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, hasItems, onClose]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`px-2.5 py-1.5 rounded-md text-sm font-normal transition-colors ${open
          ? 'bg-slate-100 dark:bg-gray-800 text-slate-900 dark:text-gray-100'
          : 'text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-slate-700'
          }`}
      >
        {label}
      </button>
      {open && hasItems && (
        <div className="absolute left-0 top-full mt-1 min-w-[180px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 z-50">
          {activeItems.map((item, index) => (
            <button
              key={`${item.label}-${index}`}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onClick?.();
                onClose();
              }}
              className={`flex w-full items-center gap-2 text-left px-3 py-1.5 text-sm transition-colors ${item.disabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-default'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
            >
              {item.icon ? (
                <span
                  className="inline-flex size-4 shrink-0 items-center justify-center text-current"
                  aria-hidden
                >
                  {item.icon}
                </span>
              ) : null}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SegmentButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-sm transition-colors ${active
        ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-gray-100 font-medium shadow-sm'
        : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 font-normal'
      }`}
  >
    {children}
  </button>
);

const ReportsIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.1" />
    <path
      d="M4.5 9V6.5M7 9V4.5M9.5 9V7.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const MindMapIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="7" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="3.5" cy="10.5" r="1.5" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="10.5" cy="10.5" r="1.5" stroke="currentColor" strokeWidth="1.1" />
    <path
      d="M7 5v2.5M5.6 9.2 6.4 7.5M8.4 9.2 7.6 7.5"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>
);

const SimulateIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 3.5v7l7-3.5L4 3.5Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const SaveIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path
      d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"
      strokeLinejoin="round"
    />
    <path d="M17 21v-8H7v8" strokeLinejoin="round" />
    <path d="M7 3v5h8" strokeLinejoin="round" />
  </svg>
);

const LoadIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" strokeLinejoin="round" />
    <path d="M14 2v6h6" strokeLinejoin="round" />
    <path d="M12 18v-6" strokeLinecap="round" />
    <path d="m9 15 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LoadLayoutIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M3 7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" strokeLinecap="round" />
    <path d="M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" strokeLinecap="round" />
    <path d="M12 3v18" strokeLinecap="round" />
    <path d="m8 12 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MoonIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7.49595 1.87402C6.75044 2.61954 6.33161 3.63067 6.33161 4.68499C6.33161 5.7393 6.75044 6.75043 7.49595 7.49595C8.24146 8.24146 9.2526 8.66029 10.3069 8.66029C11.3612 8.66029 12.3724 8.24146 13.1179 7.49595C13.1179 8.60786 12.7882 9.6948 12.1704 10.6193C11.5527 11.5438 10.6746 12.2644 9.64737 12.6899C8.6201 13.1154 7.48971 13.2268 6.39917 13.0099C5.30862 12.7929 4.30689 12.2575 3.52065 11.4712C2.73441 10.685 2.19897 9.68328 1.98205 8.59273C1.76513 7.50218 1.87646 6.3718 2.30197 5.34453C2.72748 4.31726 3.44806 3.43923 4.37258 2.82149C5.2971 2.20374 6.38404 1.87402 7.49595 1.87402Z"
      stroke="currentColor"
      strokeWidth="1.24932"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.24023 3.72461C3.24023 2.01277 4.62797 0.625001 6.33984 0.625001C8.05168 0.625001 9.43941 2.01277 9.43941 3.72461C9.43941 5.43645 8.05168 6.82422 6.33984 6.82422C4.62797 6.82422 3.24023 5.43645 3.24023 3.72461Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M0.625 12.5391C0.625 9.38281 3.18363 6.82418 6.33988 6.82418C9.49613 6.82418 12.0548 9.38281 12.0548 12.5391"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeMiterlimit="10"
      strokeLinejoin="round"
    />
  </svg>
);

const LogoutIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10 5H6.5A2.5 2.5 0 0 0 4 7.5v9A2.5 2.5 0 0 0 6.5 19H10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 8l4 4-4 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 12H9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GridIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.1" />
    <path
      d="M1.5 5.5h11M1.5 8.5h11M5.5 1.5v11M8.5 1.5v11"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>
);

export default Toolbar;
